import React, { useState } from 'react';
import axios from 'axios';
import { Camera, Trash2, ShieldCheck, Sparkles, Globe, Lock, Download, Wand2, Star, Pencil, X, Sliders } from 'lucide-react';
import { API, authHeaders, fmtErr, mediaUrl } from '../../lib/auth';
import { PROFILE_OPTIONS } from '../../mock';
import { Chips, Radios } from './Chips';

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const PhotoManager = ({ me, apply, flash }) => {
  const photos = me.photos || [];
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/user/photos`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      apply(data, 'Photo added');
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
    setBusy(false);
  };

  const remove = async (id) => {
    try {
      const { data } = await axios.delete(`${API}/user/photos/${id}`, { headers: authHeaders() });
      apply(data, 'Photo removed');
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const makeMain = async (id) => {
    try {
      const { data } = await axios.patch(`${API}/user/photos/${id}/main`, {}, { headers: authHeaders() });
      apply(data, 'Main photo updated');
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  return (
    <div className="photo-manager" data-testid="photo-manager">
      {[0, 1, 2].map((i) => {
        const p = photos[i];
        return (
          <div className={`photo-slot ${i === 0 && p ? 'main' : ''}`} key={i}>
            {p ? (
              <>
                <img src={mediaUrl(p.url, true)} alt={`Your photo ${i + 1}`} data-testid={`photo-slot-img-${i}`} />
                {i === 0 && <span className="photo-main-badge"><Star size={11} /> Main</span>}
                <div className="photo-slot-actions">
                  {i !== 0 && <button type="button" onClick={() => makeMain(p.id)} data-testid={`photo-make-main-${i}`} title="Make main photo"><Star size={13} /></button>}
                  <button type="button" onClick={() => remove(p.id)} data-testid={`photo-remove-${i}`} title="Remove photo"><Trash2 size={13} /></button>
                </div>
              </>
            ) : (
              <label className="photo-slot-empty" data-testid={`photo-slot-empty-${i}`}>
                <Camera size={20} />
                <span>{busy ? 'Uploading…' : 'Add Photo'}</span>
                <input type="file" accept="image/*" hidden disabled={busy} onChange={upload} data-testid={`photo-upload-input-${i}`} />
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ProfileView = ({ me, onEdit, onEditPrefs }) => {
  const p = me.profile || {};
  const photos = me.photos || [];
  return (
    <div className="dash-card profile-view" data-testid="profile-view">
      <div className="pv-top">
        <h3 style={{ margin: 0 }}>My Profile</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-red btn-sm" onClick={onEdit} data-testid="edit-profile-btn"><Pencil size={14} /> Edit Profile</button>
          <button className="btn btn-ghost btn-sm" onClick={onEditPrefs} data-testid="edit-preferences-shortcut-btn"><Sliders size={14} /> Edit Preferences</button>
        </div>
      </div>
      <div className="pv-gallery" data-testid="profile-view-gallery">
        {photos.length > 0 ? photos.map((ph, i) => (
          <img key={ph.id} className={`pv-photo ${i === 0 ? 'pv-main' : ''}`} src={mediaUrl(ph.url, true)} alt={`${me.name || 'Profile'} photo ${i + 1}`} data-testid={`profile-view-photo-${i}`} />
        )) : (
          <div className="pv-photo pv-main pv-empty" data-testid="profile-view-photo-empty">{(me.name || '?')[0]?.toUpperCase()}</div>
        )}
      </div>
      <div className="pv-info">
        <h2 data-testid="profile-view-name">{me.name}{p.age ? `, ${p.age}` : ''}</h2>
        <div className="pv-meta">
          {[p.city, p.country].filter(Boolean).join(', ') || 'Location not set'} · <span className="pv-kid">{me.krysha_id}</span>
          <span className={`live-pill ${me.profile_live ? 'on' : ''}`} data-testid="live-status">
            {me.profile_live ? <><Globe size={12} /> LIVE</> : <><Lock size={12} /> PRIVATE</>}
          </span>
        </div>
        {p.headline && <p className="pv-headline" data-testid="profile-view-headline">“{p.headline}”</p>}
        {p.about && <p className="pv-about" data-testid="profile-view-about">{p.about}</p>}
        <div className="pv-facts">
          {p.occupation && <div className="pv-fact"><b>Occupation</b><span>{p.occupation}</span></div>}
          {p.education && <div className="pv-fact"><b>Education</b><span>{p.education}</span></div>}
          {p.personality && <div className="pv-fact"><b>Personality</b><span>{p.personality}</span></div>}
          {me.preferences?.looking && <div className="pv-fact"><b>Looking For</b><span>{me.preferences.looking}</span></div>}
        </div>
        {p.interests?.length > 0 && (
          <div className="pv-section"><h4>Interests</h4><div className="ttags">{p.interests.map((i) => <span className="ttag" key={i}>{i}</span>)}</div></div>
        )}
        {p.lifestyle?.length > 0 && (
          <div className="pv-section"><h4>Lifestyle</h4><div className="ttags">{p.lifestyle.map((i) => <span className="ttag" key={i}>{i}</span>)}</div></div>
        )}
        {!p.age && !p.city && !p.about && (
          <p style={{ color: 'var(--muted)', fontSize: 13.5, marginTop: 10 }} data-testid="profile-view-empty-note">
            Your profile is still empty. Click Edit Profile to add your details.
          </p>
        )}
      </div>
      <p className="dash-privacy"><ShieldCheck size={13} /> Your email and mobile/WhatsApp number are never shown on the Connect page. They are only shared through the request process, with your permission.</p>
    </div>
  );
};

export const ProfileTab = ({ me, setMe, flash, goTab, startEdit }) => {
  const p = me.profile || {};
  const [mode, setMode] = useState(startEdit ? 'edit' : 'view');
  const [f, setF] = useState({
    name: me.name || '', age: p.age || '', gender: p.gender || '', city: p.city || '', country: p.country || '',
    occupation: p.occupation || '', education: p.education || '', personality: p.personality || '',
    headline: p.headline || '', about: p.about || '', interests: p.interests || [], lifestyle: p.lifestyle || [],
  });
  const [customId, setCustomId] = useState(me.krysha_id || '');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const apply = (data, msg) => { setMe(data); if (msg) flash(msg); };

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/user/profile`, {
        name: f.name,
        profile: {
          age: f.age, gender: f.gender, city: f.city, country: f.country, occupation: f.occupation,
          education: f.education, personality: f.personality, headline: f.headline, about: f.about,
          interests: f.interests, lifestyle: f.lifestyle,
        },
      }, { headers: authHeaders() });
      apply(data, 'Profile saved');
      setMode('view');
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
    setBusy(false);
  };

  const togglePhotoPublic = async (val) => {
    try {
      const { data } = await axios.patch(`${API}/user/profile`, { photo_public: val }, { headers: authHeaders() });
      apply(data, val ? 'Your photos are now shown on your public profile card' : 'Your photos are hidden from public profiles');
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const saveId = async (auto) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/user/krysha-id`, auto ? { auto: true } : { krysha_id: customId }, { headers: authHeaders() });
      setCustomId(data.krysha_id);
      apply(data, `Your Krysha ID is now ${data.krysha_id}`);
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
    setBusy(false);
  };

  const setLive = async (val) => {
    try {
      const { data } = await axios.patch(`${API}/user/visibility`, { profile_live: val }, { headers: authHeaders() });
      apply(data, val ? 'Your profile is live on Krysha. Now set your Matching Preferences.' : 'Your profile is private again.');
      if (val) goTab('preferences');
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const importForm = async () => {
    try {
      const { data } = await axios.post(`${API}/user/import-from-form`, {}, { headers: authHeaders() });
      const np = data.profile || {};
      setF((s) => ({
        ...s,
        age: np.age || s.age, gender: np.gender || s.gender, city: np.city || s.city, country: np.country || s.country,
        occupation: np.occupation || s.occupation, education: np.education || s.education,
        personality: np.personality || s.personality, about: np.about || s.about,
        interests: np.interests?.length ? np.interests : s.interests, lifestyle: np.lifestyle?.length ? np.lifestyle : s.lifestyle,
      }));
      apply(data, 'Details imported from your matchmaking form');
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const cancelEdit = () => {
    setF({
      name: me.name || '', age: p.age || '', gender: p.gender || '', city: p.city || '', country: p.country || '',
      occupation: p.occupation || '', education: p.education || '', personality: p.personality || '',
      headline: p.headline || '', about: p.about || '', interests: p.interests || [], lifestyle: p.lifestyle || [],
    });
    setMode('view');
  };

  return (
    <div style={{ display: 'grid', gap: 16 }} data-testid="profile-section">
      {mode === 'view' ? (
        <ProfileView me={me} onEdit={() => setMode('edit')} onEditPrefs={() => goTab('preferences')} />
      ) : (
        <>
          {/* Profile visibility (moved inside Edit Profile) */}
          <div className="dash-card visibility-card" data-testid="live-card">
            <h3 style={{ marginBottom: 4 }}>Profile Visibility</h3>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: -2 }}>
              {me.profile_live
                ? 'Your profile is visible in Find Connection to other live members. Private details like email and mobile are never shown.'
                : 'Your profile is Private — only you can see it. Go live to appear in Find Connection; your email and mobile always stay hidden.'}
            </p>
            <span className={`live-pill ${me.profile_live ? 'on' : ''}`} data-testid="live-status-edit" style={{ marginBottom: 10 }}>
              {me.profile_live ? <><Globe size={13} /> LIVE</> : <><Lock size={13} /> PRIVATE</>}
            </span>
            <div className="live-actions">
              <button className="btn btn-red btn-sm" onClick={() => setLive(true)} disabled={me.profile_live} data-testid="go-live-btn">Make My Profile Live</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setLive(false)} disabled={!me.profile_live} data-testid="go-private-btn">Keep My Profile Private</button>
            </div>
          </div>

          {/* Photos */}
          <div className="dash-card">
            <h3>Profile Photos</h3>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: -6 }}>Add up to 3 photos. The first one is your main photo — tap the star on any other photo to make it main.</p>
            <PhotoManager me={me} apply={apply} flash={flash} />
            <button
              type="button" className={`opt ${me.photo_public ? 'on' : ''}`} style={{ marginTop: 14 }}
              onClick={() => togglePhotoPublic(!me.photo_public)} data-testid="photo-public-toggle"
            >
              <span className="box">{me.photo_public && '✓'}</span> Show my photos on my public profile card
            </button>
            <p className="dash-privacy"><ShieldCheck size={13} /> If this is off, your photos are only visible to you and members you have accepted a connection with. JPG/PNG/WEBP up to 5 MB each.</p>
          </div>

          <div className="dash-card">
            <h3>Your Krysha ID</h3>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: -6 }}>
              Create your own ID or let us generate one for you. Format: letters, numbers and hyphens (e.g. DINESH-KRY-100001). Every ID is unique.
            </p>
            <div className="dash-search" style={{ marginTop: 12 }}>
              <input value={customId} onChange={(e) => setCustomId(e.target.value.toUpperCase())} placeholder="DINESH-KRY-100001" data-testid="krysha-id-input" />
              <button className="btn btn-red btn-sm" onClick={() => saveId(false)} disabled={busy} data-testid="krysha-id-save-btn">Save My ID</button>
              <button className="btn btn-ghost btn-sm" onClick={() => saveId(true)} disabled={busy} data-testid="krysha-id-auto-btn"><Wand2 size={14} /> Auto Generate</button>
            </div>
            <div className="dash-kid" style={{ display: 'inline-flex' }} data-testid="current-krysha-id">Current: {me.krysha_id}</div>
          </div>

          {/* Profile details */}
          <div className="dash-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <h3 style={{ margin: 0 }}>Edit Profile Details</h3>
              <button className="btn btn-ghost btn-sm" onClick={importForm} data-testid="import-form-btn"><Download size={14} /> Import from my match form</button>
            </div>
            <form onSubmit={saveProfile} style={{ marginTop: 14 }}>
              <div className="fields">
                <div className="field"><label>Full Name</label><input value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="profile-name-input" /></div>
                <div className="field"><label>Age</label><input type="number" value={f.age} onChange={(e) => set('age', e.target.value)} data-testid="profile-age-input" /></div>
                <div className="field"><label>Gender</label>
                  <select value={f.gender} onChange={(e) => set('gender', e.target.value)} data-testid="profile-gender-select">
                    <option value="">Select</option>{PROFILE_OPTIONS.gender.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="field"><label>City</label><input value={f.city} onChange={(e) => set('city', e.target.value)} data-testid="profile-city-input" /></div>
                <div className="field"><label>Country</label><input value={f.country} onChange={(e) => set('country', e.target.value)} data-testid="profile-country-input" /></div>
                <div className="field"><label>Occupation</label><input value={f.occupation} onChange={(e) => set('occupation', e.target.value)} data-testid="profile-occupation-input" /></div>
                <div className="field"><label>Highest Education</label>
                  <select value={f.education} onChange={(e) => set('education', e.target.value)} data-testid="profile-education-select">
                    <option value="">Select</option>{PROFILE_OPTIONS.education.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="field"><label>Profile Headline</label><input value={f.headline} onChange={(e) => set('headline', e.target.value)} placeholder="e.g. Chai, books and long drives" data-testid="profile-headline-input" /></div>
                <div className="field full"><label>What explains you the best?</label>
                  <Radios options={PROFILE_OPTIONS.personality} value={f.personality} onPick={(v) => set('personality', v)} testid="profile-personality" />
                </div>
                <div className="field full"><label>Interests</label>
                  <Chips options={PROFILE_OPTIONS.interests} value={f.interests} onToggle={(v) => set('interests', toggle(f.interests, v))} testid="profile-interests" />
                </div>
                <div className="field full"><label>Lifestyle</label>
                  <Chips options={PROFILE_OPTIONS.lifestyle} value={f.lifestyle} onToggle={(v) => set('lifestyle', toggle(f.lifestyle, v))} testid="profile-lifestyle" />
                </div>
                <div className="field full"><label>Short profile information</label>
                  <textarea value={f.about} onChange={(e) => set('about', e.target.value)} placeholder="A few lines about you — your vibe, weekends, what matters to you." data-testid="profile-about-input" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button className="btn btn-red btn-sm" type="submit" disabled={busy} data-testid="profile-save-btn"><Sparkles size={14} /> Save Profile</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={cancelEdit} data-testid="profile-cancel-btn"><X size={14} /> Cancel</button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileTab;
